import { Chat, FileFailureReason, Project, ProjectFile } from '@/types';

import { BlueJApi } from './blue-j-api';

export type GetProjectsResponse = {
  totalCount: number;
  projects: Project[];
}

type ProjectRequestParams = {
  id: string;
}

export type GetProjectResponse = Project;

export type CreateProjectRequest = {
  name: string;
  description?: string;
}

export type CreateProjectResponse = Project;

export type UpdateProjectRequest = {
  name: string;
  description?: string;
}

export type UpdateProjectResponse = {
  project: Project;
}

export type ProjectThread = {
  id: string;
  title: string;
  lastActiveDate: string;
  topic: string | null;
  latestChat?: Pick<Chat, "chat_id" | "question" | "answer" | "chat_type" | "created_at">;
  pinned_at?: string;
}

export type GetProjectThreadsResponse = {
  totalCount: number;
  threads: ProjectThread[];
}

type ProjectThreadRequest = {
  threadId: string;
}

type GetProjectThreadsQuery = {
  page?: number;
  perPage?: number;
}

export type GetProjectFilesResponse = {
  files: ProjectFile[];
}

type CreateProjectFileMetadataRequest = {
  filename: string;
  size: number;
  mimeType: string;
}

type CreateProjectFileMetadataResponse = {
  fileId: string;
  signedUri: string;
  method: string;
}

type GetProjectFileDownloadUrlResponse = {
  signedUri: string;
}

export type GetProjectFileDetailsResponse = ProjectFile;

export type DeleteProjectFileResponse = {
  success: boolean;
}

export class ProjectApi extends BlueJApi {
  private basePath = 'api';
  private filesBasePath = `${this.basePath}/content-management/admin/file-storage`;

  private _getProjects = this.createFetch<null, GetProjectsResponse, void>(`${this.basePath}/projects`, 'GET');

  private _getProject = this.createFetch<null, GetProjectResponse, ProjectRequestParams>(({ id }) => `${this.basePath}/projects/${id}`, 'GET');

  private _createProject = this.createFetch<CreateProjectRequest, CreateProjectResponse, void>(`${this.basePath}/projects`, 'POST');

  private _updateProject = this.createFetch<UpdateProjectRequest, UpdateProjectResponse, ProjectRequestParams>(({ id }) => `${this.basePath}/projects/${id}`, 'POST');

  private _deleteProject = this.createFetch<null, void, ProjectRequestParams>(({ id }) => `${this.basePath}/projects/${id}/delete`, 'POST');

  private _getProjectThreads = this.createFetch<GetProjectThreadsQuery, GetProjectThreadsResponse, ProjectRequestParams>(({ id }) => `${this.basePath}/projects/${id}/threads`, 'GET');

  private _addThread = this.createFetch<ProjectThreadRequest, Project, ProjectRequestParams>(({ id }) => `${this.basePath}/projects/${id}/add-thread`, 'POST');

  private _moveThread = this.createFetch<ProjectThreadRequest, Project, ProjectRequestParams>(({ id }) => `${this.basePath}/projects/${id}/move-thread`, 'POST');

  private _removeThread = this.createFetch<ProjectThreadRequest, void, ProjectRequestParams>(({ id }) => `${this.basePath}/projects/${id}/remove-thread`, 'POST');

  private _getProjectFiles = this.createFetch<null, GetProjectFilesResponse, ProjectRequestParams>(({ id }) => `${this.filesBasePath}/projects/${id}/files`, 'GET');

  private _createProjectFileMetadata = this.createFetch<CreateProjectFileMetadataRequest, CreateProjectFileMetadataResponse, ProjectRequestParams>(({ id }) => `${this.filesBasePath}/projects/${id}/file`, 'POST');

  private _updateProjectFileStatus = this.createFetch<{ status: 'processing' | 'success' | 'failed'; failureReason?: FileFailureReason }, void, { projectId: string; fileId: string }>(({ projectId, fileId }) => `${this.filesBasePath}/projects/${projectId}/files/${fileId}/status`, 'POST');

  private _getProjectFileDownloadUrl = this.createFetch<null, GetProjectFileDownloadUrlResponse, { projectId: string; fileId: string }>(
    ({ projectId, fileId }) => `${this.filesBasePath}/projects/${projectId}/files/${fileId}`,
    'GET'
  );

  private _getProjectFileDetails = this.createFetch<null, GetProjectFileDetailsResponse, { projectId: string; fileId: string }>(
    ({ projectId, fileId }) => `${this.filesBasePath}/projects/${projectId}/files/${fileId}/details`,
    'GET'
  );

  private _processProjectFile = this.createFetch<null, void, { projectId: string; fileId: string }>(
    ({ projectId, fileId }) => `${this.filesBasePath}/projects/${projectId}/files/${fileId}/process`,
    'POST'
  );

  private _deleteProjectFile = this.createFetch<null, DeleteProjectFileResponse, { projectId: string; fileId: string }>(
    ({ projectId, fileId }) => `${this.filesBasePath}/projects/${projectId}/files/${fileId}/delete`,
    'POST'
  );

  async getProjects(): Promise<GetProjectsResponse> {
    return this._getProjects().parsedResponse();
  }

  async getProject(id: string): Promise<GetProjectResponse> {
    return this._getProject(null, { id }).parsedResponse();
  }

  async createProject(project: CreateProjectRequest): Promise<CreateProjectResponse> {
    return this._createProject(project).parsedResponse();
  }

  async updateProject(id: string, project: UpdateProjectRequest): Promise<UpdateProjectResponse> {
    return this._updateProject(project, { id }).parsedResponse();
  }

  async deleteProject(id: string): Promise<void> {
    return this._deleteProject(null, { id }).parsedResponse();
  }

  async getProjectThreads(id: string, query?: GetProjectThreadsQuery): Promise<GetProjectThreadsResponse> {
    return this._getProjectThreads(query, { id }).parsedResponse();
  }

  async addThreadToProject(projectId: string, threadId: string): Promise<Project> {
    return this._addThread({ threadId }, { id: projectId }).parsedResponse();
  }

  async moveThreadToProject(projectId: string, threadId: string): Promise<Project> {
    return this._moveThread({ threadId }, { id: projectId }).parsedResponse();
  }

  async removeThreadFromProject(projectId: string, threadId: string): Promise<void> {
    return this._removeThread({ threadId }, { id: projectId }).parsedResponse();
  }

  async getProjectFiles(projectId: string): Promise<GetProjectFilesResponse> {
    return this._getProjectFiles(null, { id: projectId }).parsedResponse();
  }

  async createProjectFileMetadata(projectId: string, fileMetadata: CreateProjectFileMetadataRequest): Promise<CreateProjectFileMetadataResponse> {
    return this._createProjectFileMetadata(fileMetadata, { id: projectId }).parsedResponse();
  }

  async deleteProjectFile(projectId: string, fileId: string): Promise<DeleteProjectFileResponse> {
    return this._deleteProjectFile(null, { projectId, fileId }).parsedResponse();
  }

  async updateProjectFileStatus(projectId: string, fileId: string, status: 'processing' | 'success' | 'failed', failureReason?: FileFailureReason): Promise<void> {
    return this._updateProjectFileStatus({ status, failureReason }, { projectId, fileId }).parsedResponse();
  }

  // Attempt to mark an uploading file as failed via a keepalive request during page unload
  markProjectFileFailedOnUnload(projectId: string, fileId: string): void {
    this.fireAndForget(`${this.filesBasePath}/projects/${projectId}/files/${fileId}/status`, { status: 'failed', failureReason: 'UPLOAD_FAILED' });
  }

  async getProjectFileDownloadUrl(projectId: string, fileId: string): Promise<GetProjectFileDownloadUrlResponse> {
    return this._getProjectFileDownloadUrl(null, { projectId, fileId }).parsedResponse();
  }

  async getProjectFileDetails(projectId: string, fileId: string): Promise<GetProjectFileDetailsResponse> {
    return this._getProjectFileDetails(null, { projectId, fileId }).parsedResponse();
  }

  async processProjectFile(projectId: string, fileId: string): Promise<void> {
    return this._processProjectFile(null, { projectId, fileId }).parsedResponse();
  }
}
